/*
 * PrivacyPage — Public /privacy
 * =============================
 * Greek Privacy Policy (Πολιτική Απορρήτου) covering GDPR + Cyprus data
 * protection compliance. Linked from GDPR consent checkboxes and footer.
 */
import { Link } from "react-router-dom";
import { Shield, FileText, Mail, Trash2, Lock, User, Cookie } from "lucide-react";
import SEO from "../components/SEO";

const Section = ({ icon: Icon, title, children, testid }) => (
  <section className="mb-10" data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon size={22} className="text-[#F5A623]" />}
      <h2 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white tracking-wide">{title}</h2>
    </div>
    <div className="text-zinc-300 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPage = () => {
  const lastUpdated = "Φεβρουάριος 2026";
  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="privacy-page">
      <SEO
        title="Πολιτική Απορρήτου · Λευτεριά FC"
        description="Πολιτική Απορρήτου και προστασία προσωπικών δεδομένων της Λευτεριά FC σύμφωνα με τον GDPR και την Κυπριακή νομοθεσία."
      />

      <section className="pt-32 pb-16 px-4 sm:px-6 max-w-4xl mx-auto">
        <div className="mb-10">
          <p className="text-[#F5A623] text-xs uppercase tracking-widest mb-2">Νομικά</p>
          <h1 className="font-['Bebas_Neue'] text-4xl md:text-5xl text-white tracking-tight mb-3">
            Πολιτική Απορρήτου
          </h1>
          <p className="text-zinc-500 text-xs">Τελευταία ενημέρωση: {lastUpdated}</p>
        </div>

        <div className="bg-[#F5A623]/5 border border-[#F5A623]/30 rounded-lg p-4 mb-8 flex items-start gap-3">
          <Shield size={18} className="text-[#F5A623] flex-shrink-0 mt-0.5" />
          <p className="text-zinc-300 text-sm leading-relaxed">
            Η <strong className="text-white">Λευτεριά FC</strong> σέβεται και προστατεύει τα προσωπικά σας δεδομένα.
            Η παρούσα Πολιτική Απορρήτου εξηγεί ποια δεδομένα συλλέγουμε, γιατί, πώς τα χρησιμοποιούμε και ποια είναι τα δικαιώματά σας
            σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (ΕΕ 2016/679 — GDPR) και τον Νόμο 125(Ι)/2018 της Κυπριακής Δημοκρατίας.
          </p>
        </div>

        <Section icon={User} title="1. Υπεύθυνος Επεξεργασίας" testid="privacy-section-controller">
          <p>
            <strong className="text-white">Λευτεριά FC</strong><br />
            Λεμεσός, Κύπρος<br />
            Email: <a href="mailto:info@lefteriafc.cy" className="text-[#F5A623] hover:underline">info@lefteriafc.cy</a>
          </p>
          <p>Για ερωτήσεις σχετικά με τα δεδομένα σας, επικοινωνήστε μαζί μας στην παραπάνω διεύθυνση.</p>
        </Section>

        <Section icon={FileText} title="2. Ποια δεδομένα συλλέγουμε" testid="privacy-section-data">
          <p>Συλλέγουμε τα εξής δεδομένα ανάλογα με την αλληλεπίδρασή σας μαζί μας:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li><strong className="text-zinc-200">Στοιχεία επικοινωνίας:</strong> ονοματεπώνυμο, τηλέφωνο, email.</li>
            <li><strong className="text-zinc-200">Στοιχεία παίκτη (για ακαδημία):</strong> ηλικία, ημερομηνία γέννησης, σχολείο, στοιχεία γονέων.</li>
            <li><strong className="text-zinc-200">Ιατρικά δεδομένα (όπου απαιτείται):</strong> ιατρικό πιστοποιητικό, αλλεργίες, επείγουσα επαφή. Αυτά είναι ευαίσθητα και τα προστατεύουμε με ιδιαίτερη επιμέλεια.</li>
            <li><strong className="text-zinc-200">Έγγραφα:</strong> ταυτότητα/διαβατήριο, πιστοποιητικά γέννησης, ασφαλιστική κάλυψη, δηλώσεις εγγραφής KOP.</li>
            <li><strong className="text-zinc-200">Τεχνικά δεδομένα:</strong> διεύθυνση IP, τύπος συσκευής, cookies (ανώνυμα, μόνο μετά από συγκατάθεση).</li>
            <li><strong className="text-zinc-200">Εικόνες/βίντεο:</strong> φωτογραφίες και βίντεο από αγώνες/προπονήσεις, μόνο μετά από ρητή γραπτή συγκατάθεση για ανηλίκους.</li>
          </ul>
        </Section>

        <Section icon={Lock} title="3. Γιατί τα χρησιμοποιούμε (νομική βάση)" testid="privacy-section-purpose">
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li><strong className="text-zinc-200">Εκτέλεση σύμβασης:</strong> εγγραφή, διαχείριση συνδρομής, έκδοση παραστατικών.</li>
            <li><strong className="text-zinc-200">Έννομη υποχρέωση:</strong> εγγραφή στην Κυπριακή Ομοσπονδία Ποδοσφαίρου (KOP), λογιστικά αρχεία, φορολογικές υποχρεώσεις.</li>
            <li><strong className="text-zinc-200">Έννομο συμφέρον:</strong> ασφάλεια εγκαταστάσεων, βελτίωση υπηρεσιών, στατιστική ανάλυση.</li>
            <li><strong className="text-zinc-200">Συγκατάθεση:</strong> ενημερωτικά newsletters, marketing επικοινωνία, φωτογράφιση/βιντεοσκόπηση, μη αναγκαία cookies.</li>
          </ul>
        </Section>

        <Section icon={Shield} title="4. Πόσο διατηρούμε τα δεδομένα" testid="privacy-section-retention">
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li>Δεδομένα μελών/παικτών: για όσο διαρκεί η συμμετοχή + 5 έτη μετά τη λήξη για λογιστικούς/νομικούς σκοπούς.</li>
            <li>Ιατρικά δεδομένα: 5 έτη μετά τη λήξη συμμετοχής ή σύμφωνα με ιατρικό απόρρητο.</li>
            <li>Εγγραφές leads (φόρμες ενδιαφέροντος): 24 μήνες από την τελευταία επικοινωνία εκτός αν αιτηθείτε διαγραφή νωρίτερα.</li>
            <li>Δεδομένα cookies: όπως ορίζεται στο cookie banner (τυπικά 12 μήνες).</li>
          </ul>
        </Section>

        <Section icon={User} title="5. Τα δικαιώματά σας (GDPR)" testid="privacy-section-rights">
          <p>Έχετε τα εξής δικαιώματα, τα οποία μπορείτε να ασκήσετε δωρεάν μέσω email στο <a href="mailto:info@lefteriafc.cy" className="text-[#F5A623] hover:underline">info@lefteriafc.cy</a>:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li><strong className="text-zinc-200">Πρόσβαση:</strong> ζητήστε αντίγραφο των δεδομένων σας.</li>
            <li><strong className="text-zinc-200">Διόρθωση:</strong> ζητήστε ενημέρωση ανακριβών στοιχείων.</li>
            <li><strong className="text-zinc-200">Διαγραφή («δικαίωμα στη λήθη»):</strong> ζητήστε διαγραφή όπου δεν υπάρχει έννομη υποχρέωση διατήρησης.</li>
            <li><strong className="text-zinc-200">Περιορισμός επεξεργασίας:</strong> ζητήστε να «παγώσουμε» την επεξεργασία σε συγκεκριμένες περιπτώσεις.</li>
            <li><strong className="text-zinc-200">Φορητότητα:</strong> λάβετε τα δεδομένα σας σε μηχαναγνώσιμη μορφή.</li>
            <li><strong className="text-zinc-200">Εναντίωση:</strong> εναντιωθείτε σε επεξεργασία που βασίζεται σε έννομο συμφέρον ή marketing.</li>
            <li><strong className="text-zinc-200">Ανάκληση συγκατάθεσης:</strong> ανά πάσα στιγμή, χωρίς να επηρεάζεται η νομιμότητα επεξεργασίας πριν την ανάκληση.</li>
          </ul>
          <p className="text-zinc-400">
            Αν πιστεύετε ότι τα δικαιώματά σας δεν ικανοποιήθηκαν, μπορείτε να απευθυνθείτε στην{" "}
            <strong className="text-white">Επίτροπο Προστασίας Δεδομένων Προσωπικού Χαρακτήρα Κύπρου</strong> (<a href="https://www.dataprotection.gov.cy" target="_blank" rel="noreferrer" className="text-[#F5A623] hover:underline">dataprotection.gov.cy</a>).
          </p>
        </Section>

        <Section icon={Trash2} title="6. Ασφάλεια & Διαβίβαση" testid="privacy-section-security">
          <p>
            Τα δεδομένα σας φυλάσσονται σε ασφαλή διακομιστή με κρυπτογράφηση εν κινήσει (HTTPS) και σε ηρεμία. Η πρόσβαση περιορίζεται σε εξουσιοδοτημένο προσωπικό. Δεν πωλούμε δεδομένα σε τρίτους.
          </p>
          <p>Χρησιμοποιούμε τους εξής τρίτους παρόχους (data processors):</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li><strong className="text-zinc-200">Stripe</strong> (πληρωμές) — δεδομένα κάρτας δεν αποθηκεύονται από εμάς.</li>
            <li><strong className="text-zinc-200">Twilio</strong> (SMS ειδοποιήσεις).</li>
            <li><strong className="text-zinc-200">Meta / TikTok</strong> (social media posts — μόνο δημόσιο περιεχόμενο που έχετε ήδη εγκρίνει).</li>
            <li><strong className="text-zinc-200">Google Fonts / Analytics</strong> — μόνο με τη συγκατάθεσή σας.</li>
          </ul>
        </Section>

        <Section icon={Cookie} title="7. Cookies" testid="privacy-section-cookies">
          <p>
            Χρησιμοποιούμε τεχνικά cookies απαραίτητα για τη λειτουργία της ιστοσελίδας. Για αναλυτικά cookies και cookies marketing ζητάμε τη συγκατάθεσή σας μέσω του cookie banner που εμφανίζεται στην πρώτη επίσκεψη. Μπορείτε να ανακαλέσετε τη συγκατάθεσή σας ανά πάσα στιγμή διαγράφοντας τα cookies του browser σας.
          </p>
        </Section>

        <Section icon={User} title="8. Ανήλικοι" testid="privacy-section-minors">
          <p>
            Για ανηλίκους κάτω των 18 ετών (και ιδίως κάτω των 16 σύμφωνα με το άρθρο 8 GDPR), απαιτείται ρητή γραπτή συγκατάθεση γονέα/κηδεμόνα για κάθε επεξεργασία που δεν εμπίπτει σε έννομη υποχρέωση ή έννομο συμφέρον (π.χ. φωτογραφίες, marketing).
          </p>
        </Section>

        <Section icon={Mail} title="9. Αλλαγές & Επικοινωνία" testid="privacy-section-contact">
          <p>
            Η παρούσα Πολιτική μπορεί να αναθεωρείται περιοδικά. Για κάθε ουσιώδη αλλαγή θα σας ειδοποιήσουμε μέσω email ή ανακοίνωσης στην ιστοσελίδα.
          </p>
          <p>
            Για οποιοδήποτε ερώτημα, επικοινωνήστε στο <a href="mailto:info@lefteriafc.cy" className="text-[#F5A623] hover:underline">info@lefteriafc.cy</a>.
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-[#262626] text-center">
          <Link to="/terms" className="text-[#F5A623] hover:underline text-sm" data-testid="link-to-terms">
            Δείτε επίσης τους Όρους Χρήσης →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
